import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  Download as DownloadIcon,
  Storage as StorageIcon,
  Upload as UploadIcon
} from '@mui/icons-material';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { opportunityService } from '../services/opportunityService';
import { companyService } from '../services/companyService';
import { contactService } from '../services/contactService';
import { lobService } from '../services/lobService';
import salesPersonService from '../services/salesPersonService';
import { talentService } from '../services/talentService';
import { supportContractService } from '../services/supportContractService';
import { timeEntryService } from '../services/timeEntryService';
import { emailTemplateService } from '../services/emailTemplateService';
import getApiConfig from '../config/apiConfig';

const { apiBaseUrl } = getApiConfig();

const Admin = () => {
  const currentUser = useCurrentUser();
  const [selectedEntities, setSelectedEntities] = useState({});
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [entityCounts, setEntityCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [importEntity, setImportEntity] = useState('');
  const [importFile, setImportFile] = useState(null);

  const entities = useMemo(() => [
    { key: 'opportunities', label: 'Opportunities (with Contacts, Salespersons, Comments, Moments)', service: opportunityService, method: 'getOpportunities' },
    { key: 'companies', label: 'Companies', service: companyService, method: 'getCompanies' },
    { key: 'contacts', label: 'Contacts', service: contactService, method: 'getContacts' },
    { key: 'supportContracts', label: 'Support Contracts (with Contacts & Tickets)', service: supportContractService, method: 'getSupportContracts' },
    { key: 'talents', label: 'Talents (with Job History)', service: talentService, method: 'getTalents' },
    { key: 'timeEntries', label: 'Time Entries', service: timeEntryService, method: 'getTimeEntries' },
    { key: 'emailTemplates', label: 'Email Templates', service: emailTemplateService, method: 'getEmailTemplates' },
    { key: 'lineOfBusiness', label: 'Line of Business', service: lobService, method: 'getLOBOptions' },
    { key: 'salespersons', label: 'Salespersons', service: salesPersonService, method: 'getSalesPersons' },
    { key: 'talentCategories', label: 'Talent Categories', service: talentService, method: 'getTalentCategories' }
  ], []);

  // Check if user is authorized
  const isAuthorized = currentUser.email === 'frankie.lam@pulseinnotech.com';

  const loadEntityCounts = useCallback(async () => {
    setLoading(true);
    const counts = {};
    
    for (const entity of entities) {
      try {
        const data = await entity.service[entity.method]();
        // Handle both array responses and paginated responses
        counts[entity.key] = data.totalCount !== undefined ? data.totalCount : 
                            Array.isArray(data) ? data.length : 
                            data.data ? data.data.length : 0;
      } catch (err) {
        console.warn(`Failed to load ${entity.label}:`, err);
        counts[entity.key] = 'Error';
      }
    }
    
    setEntityCounts(counts);
    setLoading(false);
  }, [entities]);

  useEffect(() => {
    if (isAuthorized) {
      loadEntityCounts();
    }
  }, [isAuthorized, loadEntityCounts]);

  const handleEntityChange = (entityKey) => (event) => {
    setSelectedEntities(prev => ({
      ...prev,
      [entityKey]: event.target.checked
    }));
  };

  const handleSelectAll = () => {
    const allSelected = entities.every(entity => selectedEntities[entity.key]);
    const newState = {};
    entities.forEach(entity => {
      newState[entity.key] = !allSelected;
    });
    setSelectedEntities(newState);
  };

  const handleExport = async () => {
    setExporting(true);
    setError('');
    setSuccess('');

    try {
      const selectedKeys = Object.entries(selectedEntities)
        .filter(([_, selected]) => selected)
        .map(([key]) => key);

      if (selectedKeys.length === 0) {
        setError('Please select at least one entity to export');
        return;
      }

      const response = await fetch(`${apiBaseUrl}/admin/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entities: selectedKeys })
      });

      if (!response.ok) throw new Error('Export failed');
      
      const data = await response.json();
      
      // Create and download files for each entity
      selectedKeys.forEach(entity => {
        if (data[entity]) {
          const blob = new Blob([JSON.stringify(data[entity], null, 2)], { type: 'application/json' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `pulsecrm-${entity}-${new Date().toISOString().split('T')[0]}.json`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }
      });

      setSuccess('Export completed successfully');
    } catch (err) {
      console.error('Export error:', err);
      setError('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async () => {
    if (!importEntity || !importFile) {
      setError('Please select an entity and upload a file');
      return;
    }

    setImporting(true);
    setError('');
    setSuccess('');

    try {
      const fileContent = await importFile.text();
      let data;
      
      try {
        data = JSON.parse(fileContent);
      } catch (parseError) {
        throw new Error('Invalid JSON file format');
      }

      // Validate data structure
      if (!Array.isArray(data)) {
        throw new Error('Import file must contain an array of records');
      }

      if (data.length === 0) {
        throw new Error('Import file is empty');
      }

      // Show import preview
      const previewMessage = `Importing ${data.length} ${importEntity} records${getRelatedDataInfo(importEntity)}`;
      setSuccess(previewMessage);

      const response = await fetch(`${apiBaseUrl}/admin/import/${importEntity}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.details || 'Import failed');
      }

      const result = await response.json();
      setSuccess(`Import completed successfully. ${result.rowsAffected} records affected.`);
      loadEntityCounts(); // Refresh counts
    } catch (err) {
      console.error('Import error:', err);
      setError(`Failed to import data: ${err.message}`);
    } finally {
      setImporting(false);
      setImportFile(null);
      // Reset file input
      const fileInput = document.getElementById('import-file');
      if (fileInput) fileInput.value = '';
    }
  };

  const getRelatedDataInfo = (entity) => {
    switch (entity) {
      case 'opportunities':
        return ' (including contacts, salespersons, comments, and moments)';
      case 'supportContracts':
        return ' (including contacts and tickets)';
      case 'talents':
        return ' (including job history)';
      default:
        return '';
    }
  };

  if (!isAuthorized) {
    return (
      <Box sx={{ width: '100%', pr: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <AdminIcon sx={{ mr: 2, fontSize: 32 }} />
          <Typography variant="h4">Admin Panel</Typography>
        </Box>
        
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="error" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary">
            You don't have permission to access this page.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', pr: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <AdminIcon sx={{ mr: 2, fontSize: 32 }} />
        <Typography variant="h4">Admin Panel</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
          <StorageIcon /> Data Management
        </Typography>

        <Grid container spacing={3}>
          {/* Export Section */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>
              Export Data
            </Typography>
            <FormGroup>
              {entities.map(entity => (
                <FormControlLabel
                  key={entity.key}
                  control={
                    <Checkbox
                      checked={selectedEntities[entity.key] || false}
                      onChange={(e) => setSelectedEntities(prev => ({
                        ...prev,
                        [entity.key]: e.target.checked
                      }))}
                      disabled={loading || exporting}
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      {entity.label}
                      <Chip
                        size="small"
                        label={loading ? '...' : entityCounts[entity.key] || 0}
                        color="primary"
                      />
                    </Box>
                  }
                />
              ))}
            </FormGroup>
            <Box mt={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleExport}
                disabled={loading || exporting || importing}
                startIcon={exporting ? <CircularProgress size={20} /> : <DownloadIcon />}
              >
                {exporting ? 'Exporting...' : 'Export Selected'}
              </Button>
            </Box>
          </Grid>

          {/* Import Section */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>
              Import Data
            </Typography>
            <Stack spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Select Entity</InputLabel>
                <Select
                  value={importEntity}
                  onChange={(e) => setImportEntity(e.target.value)}
                  disabled={importing}
                  label="Select Entity"
                >
                  {entities.map(entity => (
                    <MenuItem key={entity.key} value={entity.key}>
                      {entity.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <input
                type="file"
                id="import-file"
                accept=".json"
                onChange={(e) => setImportFile(e.target.files?.[0])}
                disabled={importing}
                style={{ display: 'none' }}
              />
              <Button
                variant="outlined"
                onClick={() => document.getElementById('import-file')?.click()}
                disabled={importing}
              >
                {importFile ? importFile.name : 'Choose File'}
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleImport}
                disabled={!importEntity || !importFile || importing}
                startIcon={importing ? <CircularProgress size={20} /> : <UploadIcon />}
              >
                {importing ? 'Importing...' : 'Import'}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default Admin; 